import _ from 'lodash'
import { DateISO, FormGroup, Icons } from '@agro-club/agroclub-shared'
import { FormikProps } from 'formik'
import { UsContractDTO, UsTransportModes } from 'modules/domain/usContracts/types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { AButton } from 'views/components/Analytics'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { SharedModalInputs } from 'views/components/SharedModal'
import { PriceField, PricingTypeRadio } from './CommonFields'
import { QuantityInput } from 'views/components/QuantityInput/QuantityInput'
import { FormEnumSelect } from 'views/components/form/EnumSelect/FormEnumSelect'
import { DateRangePicker } from 'views/components/DateRangePicker/DateRangePicker'
import { ShortFormControl } from 'views/styled/common'
import FormSelectShared from 'views/components/FormSelectShared'
import { endpoints } from 'modules/endpoints'

type Props = {
  formik: FormikProps<UsContractDTO>
}

export const PositionFields: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('usContracts')

  return (
    <>
      {formik.values.positions.map((position, index) => {
        const prefix = `positions[${index}]`
        const range = [position.start_date as DateISO, position.end_date as DateISO]
        const isEdit = !!position.id
        return (
          <SharedModalInputs key={index}>
            <HrWithHeader text={`${t('position')}${index > 0 ? ` ${index + 1}` : ''}`} />
            <ShortFormControl>
              <FormSelectShared
                formik={formik}
                label={t('fields.symbol')}
                fieldName={`${prefix}.symbolObjectId`}
                disabled={!formik.values.product}
                endpoint={formik.values.product ? endpoints.futuresSymbolsByProduct(formik.values.product) : undefined}
                getLabel={(dto) => dto.symbol}
                searchField="symbol"
                simpleApiFormat
                placeholder=""
              />
            </ShortFormControl>
            <ShortFormControl>
              <QuantityInput formik={formik} fieldName={`${prefix}.quantity`} label={t('fields.positionQuantity')} />
            </ShortFormControl>
            <PriceField formik={formik} fieldName={`${prefix}.futures_price`} label={t('fields.futures_price')} />
            <PriceField formik={formik} fieldName={`${prefix}.basis`} label={t('fields.basis')} />
            <PriceField formik={formik} fieldName={`${prefix}.price`} label={t('fields.price')} />
            <PriceField formik={formik} fieldName={`${prefix}.freight_cost`} label={t('fields.freight_cost')} />
            <PricingTypeRadio formik={formik} fieldName={`${prefix}.pricing_type`} disabled={isEdit} />
            <FormGroup
              label={t('fields.contractDate')}
              error={_.get(formik.errors, `${prefix}.start_date`) || _.get(formik.errors, `${prefix}.end_date`)}
            >
              <DateRangePicker
                value={range}
                error={_.get(formik.errors, `${prefix}.start_date`) || _.get(formik.errors, `${prefix}.end_date`)}
                onChange={({ startDate, endDate }) => {
                  formik.setFieldValue(`${prefix}.start_date`, startDate)
                  formik.setFieldValue(`${prefix}.end_date`, endDate)
                }}
              />
            </FormGroup>
            <ShortFormControl>
              <FormEnumSelect
                label={t('fields.transportMode')}
                formik={formik}
                fieldName={`${prefix}.transport_mode`}
                translationNameSpace="usContracts:usTransportModes"
                enumType={UsTransportModes}
              />
            </ShortFormControl>
            <div>
              {formik.values.positions.length !== 1 && (
                <AButton
                  id={`deletePosition${index}`}
                  Icon={Icons.IconDelete}
                  color="destruct"
                  background="none"
                  border="solid"
                  disabled={formik.values.positions.length === 1}
                  onClick={() =>
                    formik.setFieldValue(
                      'positions',
                      formik.values.positions.filter((_, i) => i !== index),
                    )
                  }
                >
                  {t('deletePosition')}
                </AButton>
              )}
            </div>
          </SharedModalInputs>
        )
      })}
    </>
  )
}
